import { useCallback, useMemo } from 'react'

import { RoleType } from '@tribeplatform/gql-client/types'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const ActiveMembersTiny = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'actor_type', operator: '!=', value: RoleType.guest },
        { field: 'actor_type', operator: '!=', value: null },
        { field: 'person', operator: '!=', value: null },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [{ field: 'count(person)', as: 'Persons' }],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, segment, timeFrame])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="TinyArea"
          title={$t({
            id: 'Analytics.ActiveMembersTiny.ChartTitle',
            defaultMessage: 'Active {MEMBERS}',
            description: 'Title for tiny active members chart',
          })}
          description={timeFrame.text}
          toolTipText={$t({
            id: 'Analytics.ActiveMembersTiny.ChartTooltipText',
            defaultMessage:
              '{MEMBERS_CC} that visited the {COMMUNITY} in the timeframe',
            description: 'Active members tiny chart tooltip text',
          })}
          current={getValueFromAnalyticsData(data[1], 'Persons')}
          previous={getValueFromAnalyticsData(data[2], 'Persons')}
          data={data[0]}
          timeFrame={timeFrame}
          indexBy={['Persons']}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
